import { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Alert } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { login, me } from '@/api/auth';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);

  const target = (location.state as LocationState | null)?.from?.pathname || '/dashboard';

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    setWarn(null);
    try {
      const res = await login(values);
      setAuth(res.token, res.admin);
      // Best-effort profile refresh; failure is non-fatal.
      try {
        const profile = await me();
        setAuth(res.token, profile);
      } catch {
        /* swallow */
      }
      message.success('登录成功');
      navigate(target, { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : '登录失败';
      // Backend M1 does NOT yet expose /admin/v1/auth/login. Provide a dev
      // bypass so the SPA is usable today against the dev placeholder auth.
      if (
        import.meta.env.DEV &&
        (msg.includes('501') ||
          msg.includes('NOT_IMPLEMENTED') ||
          msg.includes('404') ||
          msg.includes('NOT_FOUND') ||
          msg.toLowerCase().includes('not found') ||
          msg.toLowerCase().includes('network'))
      ) {
        setAuth('dev-placeholder-token', {
          id: 'admin_dev',
          username: values.username || 'dev',
          display_name: '开发模式',
          role: 'super',
          scope: 'admin',
        });
        setWarn('登录接口尚未实现，已使用开发占位 token 进入。');
        navigate(target, { replace: true });
        return;
      }
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f4f7fb 0%, #e8edf5 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{ width: 380, boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)' }}
        styles={{ body: { padding: 28 } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            touliu 平台后台
          </Typography.Title>
          <Typography.Text type="secondary">PM / 运营 / 美术 / ML 内部工具</Typography.Text>
        </div>
        {warn && (
          <Alert type="warning" message={warn} showIcon style={{ marginBottom: 16 }} />
        )}
        <Form layout="vertical" onFinish={onFinish} initialValues={{ username: '', password: '' }}>
          <Form.Item
            name="username"
            label="账号"
            rules={[{ required: true, message: '请输入账号' }, { max: 64 }]}
          >
            <Input prefix={<UserOutlined />} size="large" autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }, { max: 128 }]}
          >
            <Input.Password prefix={<LockOutlined />} size="large" autoComplete="current-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              登录
            </Button>
          </Form.Item>
        </Form>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, textAlign: 'center', marginBottom: 0 }}>
          忘记密码？联系平台 SRE 重置。
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
